import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

export interface CabinaMessage {
  id: number;
  from: string;
  text: string;
  time: string;
  type: 'message' | 'song_request' | 'shoutout' | 'contest' | 'voice';
  avatarUrl?: string;
}

interface CabinaContextType {
  isCabinaOpen: boolean;
  setCabinaOpen: (open: boolean) => void;
  messages: CabinaMessage[];
  addMessage: (msg: Omit<CabinaMessage, 'id' | 'time'>) => void;
  seedMessages: (msgs: CabinaMessage[]) => void;
}

const CabinaContext = createContext<CabinaContextType | null>(null);

export function CabinaProvider({ children }: { children: ReactNode }) {
  const [isCabinaOpen, setCabinaOpen] = useState(false);
  const [messages, setMessages] = useState<CabinaMessage[]>([]);

  const addMessage = useCallback((msg: Omit<CabinaMessage, 'id' | 'time'>) => {
    const newMsg: CabinaMessage = {
      ...msg,
      id: Date.now(),
      time: new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }),
    };
    setMessages((prev) => [...prev, newMsg]);
  }, []);

  const seedMessages = useCallback((msgs: CabinaMessage[]) => {
    setMessages(msgs);
  }, []);

  return (
    <CabinaContext.Provider value={{ isCabinaOpen, setCabinaOpen, messages, addMessage, seedMessages }}>
      {children}
    </CabinaContext.Provider>
  );
}

export function useCabinaOpen(): CabinaContextType {
  const ctx = useContext(CabinaContext);
  if (!ctx) {
    return { isCabinaOpen: false, setCabinaOpen: () => {}, messages: [], addMessage: () => {}, seedMessages: () => {} };
  }
  return ctx;
}

export function useCabinaMessages() {
  const ctx = useContext(CabinaContext);
  if (!ctx) return { messages: [] as CabinaMessage[], addMessage: (_msg: Omit<CabinaMessage, 'id' | 'time'>) => {}, seedMessages: (_msgs: CabinaMessage[]) => {} };
  return { messages: ctx.messages, addMessage: ctx.addMessage, seedMessages: ctx.seedMessages };
}